import React, { useState, useEffect } from 'react';
import { Crown, Zap, Check, Tag, ArrowRight, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { apiFetch, apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

export default function Upgrade() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const data = await apiGet(API_ROUTES.public.plans, { auth: false });
      // Only show paid plans (priceMonthly > 0)
      setPlans((data.plans || []).filter(p => p.priceMonthly > 0));
    } catch {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (plan) => billingCycle === 'yearly' ? plan.priceYearly : plan.priceMonthly;

  const validateCoupon = async () => {
    if (!couponCode.trim() || !selectedPlan) return;
    setValidatingCoupon(true);
    setCouponResult(null);
    try {
      const res = await apiFetch(`${API_ROUTES.app.coupons}/validate`, {
        method: 'POST',
        body: {
          code: couponCode,
          planSlug: selectedPlan.slug,
          amount: getPrice(selectedPlan),
        },
      });
      const data = await res.json();
      setCouponResult(data);
      if (!data.valid) toast.error(data.detail);
    } catch {
      toast.error('Failed to validate coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleUpgrade = async () => {
    if (!selectedPlan) { toast.error('Select a plan'); return; }
    setSubmitting(true);
    try {
      const res = await apiFetch(`${API_ROUTES.app.payments}/create-invoice`, {
        method: 'POST',
        body: {
          planSlug: selectedPlan.slug,
          billingCycle,
          couponCode: couponResult?.valid ? couponCode : undefined,
        },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed');
      }
      const data = await res.json();
      // Redirect to Cryptomus payment page
      window.location.href = data.paymentUrl;
    } catch (e) {
      toast.error(e.message || 'Payment failed');
    } finally {
      setSubmitting(false);
    }
  };

  const finalAmount = couponResult?.valid ? couponResult.finalAmount : (selectedPlan ? getPrice(selectedPlan) : 0);

  if (user?.plan && user.plan !== 'free') {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <Crown size={48} className="mx-auto text-amber-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-100 mb-2">You're on the {user.plan} plan</h2>
        <p className="text-gray-500">Your subscription is active. Manage it from Settings.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-100 mb-2">Upgrade Your Plan</h1>
        <p className="text-gray-500">Unlock unlimited accounts, Monte Carlo, PDF reports & more</p>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3 mb-8">
        <button
          onClick={() => setBillingCycle('monthly')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'text-gray-500 hover:text-gray-300'}`}
        >Monthly</button>
        <button
          onClick={() => setBillingCycle('yearly')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${billingCycle === 'yearly' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'text-gray-500 hover:text-gray-300'}`}
        >
          Yearly <span className="text-xs text-emerald-400 ml-1">Save 17%</span>
        </button>
      </div>

      {/* Plans */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading plans...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {plans.map((plan) => {
            const price = getPrice(plan);
            const isSelected = selectedPlan?.id === plan.id;
            let features = {};
            try { features = typeof plan.features === 'string' ? JSON.parse(plan.features) : plan.features; } catch {}

            return (
              <div
                key={plan.id}
                onClick={() => { setSelectedPlan(plan); setCouponResult(null); }}
                className={`cursor-pointer rounded-2xl border p-6 transition-all hover:-translate-y-0.5 ${
                  isSelected ? 'border-emerald-500/50 bg-emerald-500/5 shadow-lg shadow-emerald-500/10' : 'border-dark-border bg-dark-card hover:border-white/15'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                  {isSelected && <Check size={20} className="text-emerald-400" />}
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-white">${price}</span>
                  <span className="text-gray-500 ml-1">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-300"><Check size={14} className="text-emerald-400" />{plan.maxAccounts >= 999 ? 'Unlimited' : plan.maxAccounts} accounts</li>
                  <li className="flex items-center gap-2 text-sm text-gray-300"><Check size={14} className="text-emerald-400" />{plan.maxApiKeys} API keys</li>
                  {Object.entries(features).filter(([, v]) => v).map(([k]) => (
                    <li key={k} className="flex items-center gap-2 text-sm text-gray-300"><Check size={14} className="text-emerald-400" />{k.replace(/([A-Z])/g, ' $1').trim()}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Coupon + Pay */}
      {selectedPlan && (
        <div className="bg-dark-card border border-dark-border rounded-xl p-6">
          {/* Coupon input */}
          <div className="flex items-center gap-3 mb-5">
            <Tag size={16} className="text-purple-400" />
            <input
              type="text"
              placeholder="Coupon code (optional)"
              value={couponCode}
              onChange={(e) => { setCouponCode(e.target.value); setCouponResult(null); }}
              className="flex-1 bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200"
            />
            <button
              onClick={validateCoupon}
              disabled={!couponCode.trim() || validatingCoupon}
              className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg text-sm hover:bg-purple-500/30 disabled:opacity-50"
            >
              {validatingCoupon ? 'Checking...' : 'Apply'}
            </button>
          </div>

          {/* Coupon result */}
          {couponResult && (
            <div className={`mb-5 px-4 py-2 rounded-lg text-sm ${couponResult.valid ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
              {couponResult.valid
                ? `✓ ${couponResult.discountType === 'percent' ? `${couponResult.discountValue}%` : `$${couponResult.discountValue}`} off — you save $${couponResult.discountAmount}`
                : couponResult.detail}
            </div>
          )}

          {/* Summary + Pay button */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total</p>
              <p className="text-2xl font-bold text-white">
                ${finalAmount}
                {couponResult?.valid && <span className="text-sm text-gray-500 line-through ml-2">${getPrice(selectedPlan)}</span>}
              </p>
            </div>
            <button
              onClick={handleUpgrade}
              disabled={submitting}
              className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold px-6 py-3 rounded-xl hover:shadow-lg hover:shadow-emerald-500/30 disabled:opacity-50 transition-all"
            >
              {submitting ? <Loader size={18} className="animate-spin" /> : <ArrowRight size={18} />}
              {submitting ? 'Creating...' : 'Pay with Crypto'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
